import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleLoginButton } from './GoogleLoginButton';
import { PrivacyPolicy } from '../PrivacyPolicy';
import { TermsOfService } from '../TermsOfService';
import { FormGroup, FormLabel, FormInput, FormButton } from './FormComponents';
import './AuthForm.css';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { register, error, clearError } = useAuth();

  // Check if Google OAuth is configured
  const isGoogleConfigured = process.env.REACT_APP_GOOGLE_CLIENT_ID && 
    process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'placeholder-get-from-google-console';

  const validateForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.firstName.trim()) {
      errors.push('First name is required');
    }
    
    if (!formData.lastName.trim()) {
      errors.push('Last name is required');
    }
    
    if (!formData.email.trim()) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push('Email format is invalid');
    }
    
    if (!formData.password) {
      errors.push('Password is required');
    } else if (formData.password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setValidationErrors([]);
    clearError();

    try {
      await register(
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.password,
        formData.confirmPassword
      );
      // Success - show success message and redirect to login page
      setRegistrationSuccess(true);
      setTimeout(() => {
        onSwitchToLogin();
      }, 1500); // Show success message for 1.5 seconds before redirecting
    } catch (error) {
      // Error is handled by the context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
    if (error) {
      clearError();
    }
  };

  const allErrors = [...validationErrors];
  if (error) {
    allErrors.push(error);
  }

  return (
    <div className="auth-form-container">
      <h2 className="auth-form-title">Register</h2>
      
      {registrationSuccess && (
        <div className="auth-success-message">
          Registration successful! Redirecting to login...
        </div>
      )}
      
      {allErrors.length > 0 && !registrationSuccess && (
        <div className="auth-error-message">
          {allErrors.map((err, index) => (
            <div key={index}>{err}</div>
          ))}
        </div>
      )}

      {!registrationSuccess && (
        <>
          {/* Google Login Button - only show if configured */}
          {isGoogleConfigured && (
            <>
              <GoogleLoginButton
                text="signup_with"
                onSuccess={() => {
                  // Success handled by AuthContext
                }}
                onError={(error) => {
                  console.error('Google signup error:', error);
                }}
              />

              <div className="auth-divider">
                <span className="auth-divider-text">OR</span>
              </div>
            </>
          )}
        </>
      )}

      <form onSubmit={handleSubmit} className="auth-form">
        <FormGroup>
          <FormLabel htmlFor="firstName">
            First Name
          </FormLabel>
          <FormInput
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your first name"
            maxLength={100}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="lastName">
            Last Name
          </FormLabel>
          <FormInput
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your last name"
            maxLength={100}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="email">
            Email
          </FormLabel>
          <FormInput
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your email"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="password">
            Password
          </FormLabel>
          <FormInput
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your password"
            minLength={6}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="confirmPassword">
            Confirm Password
          </FormLabel>
          <FormInput
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            disabled={isSubmitting}
            placeholder="Confirm your password"
            minLength={6}
          />
        </FormGroup>

        <FormButton
          type="submit"
          disabled={isSubmitting}
          variant="primary"
        >
          {isSubmitting ? 'Creating Account...' : 'Register'}
        </FormButton>
      </form>

      <div className="auth-switch-container">
        <p>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="auth-switch-button"
            disabled={isSubmitting}
          >
            Login Here
          </button>
        </p>
        
        <div className="auth-legal-links">
          <p className="legal-text">
            By registering, you agree to our{' '}
            <button 
              type="button"
              onClick={() => setShowPrivacyModal(true)}
              className="legal-link"
            >
              Privacy Policy
            </button>
            {' '}and{' '}
            <button 
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="legal-link"
            >
              Terms of Service
            </button>
          </p>
        </div>
      </div>

      {showPrivacyModal && (
        <PrivacyPolicy 
          isModal={true} 
          onClose={() => setShowPrivacyModal(false)} 
        />
      )}

      {showTermsModal && (
        <TermsOfService 
          isModal={true} 
          onClose={() => setShowTermsModal(false)} 
        />
      )}
    </div>
  );
};